import { AlertCircle, Loader2, type LucideIcon } from "lucide-react";

import type { DealTransformed } from "@/lib/schema/deal-schema";
import { getLastActivityDate } from "@/services/deals-service";
import type { SortField, SortDirection } from "@/store/deals-view-store";
import { getField } from "./fields";

// ─── Analysis predicates ──────────────────────────────────────────────
// Each enforces "score === 0 returns true" because 0 is a valid analyzed result,
// distinct from "no analysis has run yet" (which is null/undefined).

export function hasFitScore(deal: DealTransformed): boolean {
  return deal.aiGenerations?.deal_fit?.[0]?.deal_fit?.overall?.score != null;
}

export function hasGapAnalysis(deal: DealTransformed): boolean {
  return deal.aiGenerations?.gap_analysis?.[0]?.gap_analysis_score != null;
}

export function hasAnalysis(deal: DealTransformed): boolean {
  return hasFitScore(deal) || hasGapAnalysis(deal);
}

// ─── Fit Score helpers ────────────────────────────────────────────────

export function getFitScore(deal: DealTransformed): number | null {
  const score = deal.aiGenerations?.deal_fit?.[0]?.deal_fit?.overall?.score;
  return score != null ? score : null;
}

export function getGapAnalysisScore(deal: DealTransformed): number | null {
  const score = deal.aiGenerations?.gap_analysis?.[0]?.gap_analysis_score;
  return score != null ? score : null;
}

export type FitScoreBucket = "strong" | "medium" | "weak" | "unanalyzed";

export function getFitScoreBucket(score: number | null): FitScoreBucket {
  if (score == null) return "unanalyzed";
  if (score >= 70) return "strong";
  if (score >= 40) return "medium";
  return "weak";
}

// ─── Report status indicator ──────────────────────────────────────────
// Single source for the deal-row report spinner shown across the table,
// kanban and mobile views. Returns the icon to render plus a human label
// for its tooltip, or null when no report is generating or failed.

// Friendly names for the report_status keys we recognise. Anything not
// listed falls back to a humanised key, so the tooltip stays readable even
// as new report types are added BE-side.
const REPORT_LABELS: Record<string, string> = {
  tearsheet: "tearsheet",
  deal_info: "deal info",
  deal_info_ingest: "deal info",
  deal_fit: "deal fit",
  gap_analysis: "gap analysis",
  datasufficiencyscore_v2: "data sufficiency",
  datasufficiencyscore: "data sufficiency",
  website_summary_v2: "website summary",
  website_summary_v2_ingest: "website summary",
  website_summary: "website summary",
  website_summary_ingest: "website summary",
  insights: "insights",
  metrics: "metrics",
  startupscore: "startup score",
  summary: "summary",
  migration: "migration",
  linkedin_company_scrape: "LinkedIn data",
};

const humanizeReportKey = (key: string) =>
  REPORT_LABELS[key] ?? key.replace(/_/g, " ");

export interface ReportStatusIndicator {
  type: "loading" | "failed";
  icon: LucideIcon;
  label: string;
}

export function getReportStatusIndicator(
  reportStatus: Record<string, string> | null | undefined
): ReportStatusIndicator | null {
  if (!reportStatus) return null;

  // BE writes "pending" while a report is queued for the worker and
  // emits "generating_*" intermediate states for V2 reports
  // (generating_dealinfo, generating_tearsheet, etc). Iterate every
  // status value so V2 / ingest / gap_analysis / deal_fit reports are
  // covered without a hardcoded allowlist that drifts as schemas grow.
  const entries = Object.entries(reportStatus).filter(
    (e): e is [string, string] => typeof e[1] === "string"
  );

  const isActive = (v: string) =>
    v === "in_progress" || v === "pending" || v.startsWith("generating_");

  const namesFor = (predicate: (v: string) => boolean) => [
    ...new Set(
      entries.filter(([, v]) => predicate(v)).map(([k]) => humanizeReportKey(k))
    ),
  ];

  const active = namesFor(isActive);
  if (active.length) {
    return {
      type: "loading",
      icon: Loader2,
      label: `Generating ${active.join(", ")}…`,
    };
  }

  const failed = namesFor((v) => v === "failed");
  if (failed.length) {
    const joined = failed.join(", ");
    return {
      type: "failed",
      icon: AlertCircle,
      label: `${joined.charAt(0).toUpperCase()}${joined.slice(1)} generation failed`,
    };
  }

  return null;
}

// ─── Comparator ───────────────────────────────────────────────────────
// Pure function. Pass workspace-derived context (e.g. statusPriority) when
// sorting by status. Nulls always sink to the bottom regardless of direction.

export interface CompareContext {
  statusPriority?: Record<string, number>;
}

export function compareDeals(
  a: DealTransformed,
  b: DealTransformed,
  field: SortField,
  direction: SortDirection,
  ctx: CompareContext = {}
): number {
  const dir = direction === "asc" ? 1 : -1;

  switch (field) {
    case "name": {
      // Coerce null/undefined names to empty string so drafts or just-created
      // deals (where name may be missing) don't crash the sort.
      return (
        (a.overview.name ?? "").localeCompare(b.overview.name ?? "") * dir
      );
    }
    case "fitScore": {
      return compareNullable(getFitScore(a), getFitScore(b), dir);
    }
    case "gapAnalysis": {
      return compareNullable(getGapAnalysisScore(a), getGapAnalysisScore(b), dir);
    }
    case "added": {
      const tA = new Date(a.overview.addedOn).getTime();
      const tB = new Date(b.overview.addedOn).getTime();
      return (tA - tB) * dir;
    }
    case "lastActivity": {
      const tA = getLastActivityDate(a).getTime();
      const tB = getLastActivityDate(b).getTime();
      return (tA - tB) * dir;
    }
    case "status": {
      if (!ctx.statusPriority) return 0;
      const pA = ctx.statusPriority[a.overview.status] ?? Number.MAX_SAFE_INTEGER;
      const pB = ctx.statusPriority[b.overview.status] ?? Number.MAX_SAFE_INTEGER;
      return (pA - pB) * dir;
    }
    default:
      // Anything else the registry marks sortable. The cases above stay because
      // they carry behaviour a generic accessor cannot express — status orders
      // by the workspace's configured position, not alphabetically, and
      // lastActivity is derived from a dozen timestamps.
      return compareByField(a, b, field, dir);
  }
}

/**
 * Generic comparison via the field registry, so marking a field `sortable`
 * there is enough to make the header work.
 *
 * Nulls sink to the bottom in both directions, matching `compareNullable` — a
 * deal with no location should not outrank one that has it just because you
 * clicked descending.
 */
function compareByField(
  a: DealTransformed,
  b: DealTransformed,
  field: string,
  dir: 1 | -1
): number {
  const def = getField(field);
  if (!def) return 0;

  const vA = def.accessor(a);
  const vB = def.accessor(b);

  const emptyA = vA == null || vA === "";
  const emptyB = vB == null || vB === "";
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  if (vA instanceof Date && vB instanceof Date) {
    return (vA.getTime() - vB.getTime()) * dir;
  }
  if (typeof vA === "number" && typeof vB === "number") {
    return (vA - vB) * dir;
  }
  if (typeof vA === "boolean" && typeof vB === "boolean") {
    return (Number(vA) - Number(vB)) * dir;
  }
  return String(vA).localeCompare(String(vB)) * dir;
}

// Numeric compare with nulls always last (regardless of direction).
function compareNullable(
  a: number | null,
  b: number | null,
  dir: 1 | -1
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a - b) * dir;
}
